import { type ChangeEvent, createContext, type FC, type MouseEvent, type ReactElement, useState } from 'react';
import { usePagination } from '../content/hooks';
import type {
  ListBarModel,
  ListCellarModel,
  Order,
  RemoteCoolerModel,
  ServiceModel,
  SubscriptionModel,
  VenueFiltersProps,
  VenueListModel,
  VenueSortingFields
} from '../models';

// Main Venues Context Interface
interface IVenuesContext {
  // Venues
  venues: VenueListModel[];
  setVenues: (val: VenueListModel[]) => void;
  refreshVenuesData: boolean;
  toggleVenuesUpdate: () => void;
  page: number;
  setPage: (val: number) => void;
  limit: number;
  setLimit: (val: number) => void;
  count: number;
  setCount: (val: number) => void;
  order: Order;
  setOrder: (val: Order) => void;
  orderBy: VenueSortingFields;
  setOrderBy: (val: VenueSortingFields) => void;
  handlePageChange: (event: any, newPage: number) => void;
  handleLimitChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleRequestSort: (event: MouseEvent, property: VenueSortingFields) => void;
  filters: VenueFiltersProps;
  setFilters: (val: VenueFiltersProps) => void;
  resetFilters: () => void;

  // Shared data (used by nested components for dropdowns/selects)
  allRemotes: RemoteCoolerModel[];
  setAllRemotes: (val: RemoteCoolerModel[]) => void;
  allCellars: ListCellarModel[];
  setAllCellars: (val: ListCellarModel[]) => void;
  allBars: ListBarModel[];
  setAllBars: (val: ListBarModel[]) => void;
  subscriptions: SubscriptionModel[];
  setSubscriptions: (val: SubscriptionModel[]) => void;
  services: ServiceModel[];
  setServices: (val: ServiceModel[]) => void;
}

export const VenuesContext = createContext<IVenuesContext>({} as IVenuesContext);

export const VenuesProvider: FC<{ children: ReactElement }> = ({ children }) => {
  // Venues - main list pagination
  const [venues, setVenues] = useState<VenueListModel[]>([]);
  const [refreshVenuesData, setRefreshVenuesData] = useState(false);
  const toggleVenuesUpdate = (): void => setRefreshVenuesData(!refreshVenuesData);
  const venuesPagination = usePagination<VenueSortingFields>(toggleVenuesUpdate, 'name', 'asc');

  // Venue filters
  const emptyFilters: VenueFiltersProps = {};
  const [filters, setFilters] = useState<VenueFiltersProps>(emptyFilters);
  const resetFilters = (): void => {
    setFilters(emptyFilters);
  };

  // Shared data (used by nested entity components for dropdowns/selects)
  const [subscriptions, setSubscriptions] = useState<SubscriptionModel[]>([]);
  const [services, setServices] = useState<ServiceModel[]>([]);
  const [allCellars, setAllCellars] = useState<ListCellarModel[]>([]);
  const [allBars, setAllBars] = useState<ListBarModel[]>([]);
  const [allRemotes, setAllRemotes] = useState<RemoteCoolerModel[]>([]);

  return (
    <VenuesContext.Provider
      value={{
        // Venues
        filters,
        setFilters,
        resetFilters,
        refreshVenuesData,
        toggleVenuesUpdate,
        venues,
        setVenues,
        page: venuesPagination.page,
        setPage: venuesPagination.setPage,
        limit: venuesPagination.limit,
        setLimit: venuesPagination.setLimit,
        count: venuesPagination.count,
        setCount: venuesPagination.setCount,
        order: venuesPagination.order,
        setOrder: venuesPagination.setOrder,
        orderBy: venuesPagination.orderBy,
        setOrderBy: venuesPagination.setOrderBy,
        handlePageChange: venuesPagination.handlePageChange,
        handleLimitChange: venuesPagination.handleLimitChange,
        handleRequestSort: venuesPagination.handleRequestSort,
        // Shared data
        allCellars,
        setAllCellars,
        allBars,
        setAllBars,
        allRemotes,
        setAllRemotes,
        services,
        setServices,
        setSubscriptions,
        subscriptions
      }}
    >
      {children}
    </VenuesContext.Provider>
  );
};
